import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { useEffect, useState } from "react";
import { FiEdit, FiTool, FiTrash2 } from "react-icons/fi";
import CreateButton from "../../components/CreateButton";
import InfoBanner from "../../components/InfoBanner";
import { useAuth } from "../../hooks/useAuth";
import api from "../../services/api";
import {
  ActionButton,
  ActionsCell,
  Container,
  SchedulingSearchCard,
  StatusMyNotify,
  StatusToolsCell,
  Table,
  TableContainer,
  TextFeldClientSearch,
  Title
} from "./tools.styled";

import { ToolsInterface } from "../../common/interfaces";

import CustomTooltip from "../../components/CustomTooltip";
import {
  ToolsConditionStatusLabel,
  TypeToolsConditionStatus
} from "../../components/enum/toolsStatusEnum";
import {
  TypeActiveStatus,
  TypeActiveStatusLabel
} from "../../components/enum/typeActiveStatusEnum";
import HelpVideoButton from "../../components/HelpVideoButton";
import ToolsCreateModal from "../../components/modal/ToolsCreateModal";
import PaginationControls from "../../components/PaginationControls";
import StatusSwitch from "../../components/switch/StatusSwitch";

import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import PageToaster from "../../components/toaster/PageToaster";
import { usePageToast } from "../../hooks/usePageToast";

export const ToolsPage = () => {
  const [search, setSearch] = useState("");

  const [tools, setTools] = useState<ToolsInterface[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Controle do diálogo de confirmação de exclusão
  const [dialogOpen, setDialogOpen] = useState(false);

  const [toolsToDelete, setToolsToDelete] = useState<number | null>(null);

  // **Controle do modal de criação/edição**
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedTools, setSelectedSTools] = useState<ToolsInterface | null>(
    null
  );

  const [toolsCondition, setToolsCondition] =
    useState<TypeToolsConditionStatus>(TypeToolsConditionStatus.NONE);

  const [toolsStatus, setToolsStatus] = useState<TypeActiveStatus>(
    TypeActiveStatus.NONE
  );

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const containerId = "toaster-tools-page";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const handleHttpError = makeToasterErrorHandler(autoError);

  const { isAdmin, isUser } = useAuth();

  // Busca agendamentos paginados
  const fetchToolss = async (currentPage: number) => {
    let current_status;

    if (toolsStatus === TypeActiveStatus.ACTIVE) {
      current_status = true;
    } else if (toolsStatus === TypeActiveStatus.INACTIVE) {
      current_status = false;
    } else {
      current_status = undefined;
    }

    try {
      const response = await api.get(`/tools`, {
        params: {
          page: currentPage,
          limit: 8,
          search: search.trim() !== "" ? search.trim() : undefined,
          initial_date: startDate || undefined,
          final_date: endDate || undefined,
          tools_status:
            toolsCondition !== TypeToolsConditionStatus.NONE
              ? toolsCondition
              : undefined,
          showActives: current_status
        }
      });

      setTools(response.data.items);
      setTotalPages(response.data.meta.totalPages);
    } catch (error) {
      handleHttpError(error);
      console.error("Erro ao buscar ferramentas:", error);
    }
  };

  // Abre diálogo de confirmação antes de excluir agendamento
  const openDeleteDialog = (tools: ToolsInterface) => {
    setToolsToDelete(tools.tools_id);
    setDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDialogOpen(false);
    setToolsToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (toolsToDelete !== null) {
      try {
        await api.delete(`/tools/${toolsToDelete}`);
        autoSuccess(`Ativo removido com sucesso!`);
        fetchToolss(page);
      } catch (error) {
        handleHttpError(error);
        console.error("Erro ao deletar ferramentas:", error);
      } finally {
        setDialogOpen(false);
        setToolsToDelete(null);
      }
    }
  };

  // ***********************
  // 1) Criar um handle que abre o modal em modo “novo agendamento”
  // ***********************
  const handleOpenCreateModal = () => {
    setSelectedSTools(null); // garante que o modal abra em branco
    setIsModalOpen(true);
  };

  // ***********************
  // 2) Criar um handle que abre o modal em modo “edição”, já carregando os dados
  // ***********************
  const handleOpenEditModal = (tools: ToolsInterface) => {
    setSelectedSTools(tools);
    setIsModalOpen(true);
  };

  // ***********************
  // 3) Recarrega lista e fecha modal quando ele for fechado
  // ***********************
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSTools(null);
    fetchToolss(page);
  };

  const handleToggleStatus = async (tools: ToolsInterface) => {
    try {
      let current_status = !tools.tools_status;
      await api.patch(`/tools/${tools.tools_id}`, {});

      if (current_status) {
        autoSuccess("Ativo ativada com sucesso!");
      } else {
        autoWarn("Ativo desativada com sucesso!");
      }

      fetchToolss(page); // Atualiza a lista após o toggle
    } catch (error) {
      handleHttpError(error);
      console.error("Erro ao atualizar status do cliente:", error);
    }
  };

  useEffect(() => {
    fetchToolss(page);
  }, [page, search, startDate, endDate, toolsCondition, toolsStatus]);

  return (
    <Container>
      <PageToaster containerId={containerId} />
      <Title>
        Lista de Ativos
        <CreateButton
          onClick={handleOpenCreateModal}
          style={{ marginLeft: "14px" }}
        >
          <FiTool size={18} /> Criar Ativo
        </CreateButton>
        <HelpVideoButton
          youtubeUrl="https://youtu.be/yK3KBs9Z3RA"
          modalTitle="Gerenciar Ativos"
          sx={{
            position: "absolute",
            top: 0,
            right: 8,
            zIndex: 2
          }}
        />
      </Title>

      <InfoBanner icon={<FiTool size={26} />} title="Ativos">
        Cadastro das ferramentas e equipamentos da empresa. Aqui você controla
        o que existe no patrimônio, o estado de cada item e quem está com ele
        emprestado no momento.
      </InfoBanner>

      <SchedulingSearchCard>
        <TextField
          label="Data de Início"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            max: new Date().toLocaleDateString("en-CA") // impede datas futuras no fuso local
          }}
        />

        <TextField
          disabled={!startDate}
          label="Data Final"
          type="date"
          value={startDate ? endDate : ""}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: startDate || undefined, // impede menor que startDate
            max: new Date().toLocaleDateString("en-CA") // impede datas futuras
          }}
        />

        <TextFeldClientSearch
          id="tools_search"
          label="Buscar Ativos"
          variant="outlined"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: "300px" }}
        />

        <FormControl>
          <InputLabel id="label-status">Condição</InputLabel>
          <Select
            sx={{ width: "200px" }}
            labelId="label-status"
            value={toolsCondition}
            label="Condição"
            onChange={(e) =>
              setToolsCondition(e.target.value as TypeToolsConditionStatus)
            }
          >
            {(
              Object.values(
                TypeToolsConditionStatus
              ) as TypeToolsConditionStatus[]
            )
              .filter((code) => code !== TypeToolsConditionStatus.NONE)
              .map((statusCode) => (
                <MenuItem key={statusCode} value={statusCode}>
                  {ToolsConditionStatusLabel[statusCode]}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <FormControl>
          <InputLabel id="label-status">Status</InputLabel>
          <Select
            sx={{ width: "200px" }}
            labelId="label-status"
            value={toolsStatus}
            label="Status"
            onChange={(e) => setToolsStatus(e.target.value as TypeActiveStatus)}
          >
            {(Object.values(TypeActiveStatus) as TypeActiveStatus[])
              // .filter(
              //     (code) => code !== TypeActiveStatus.NONE
              // )
              .map((statusCode) => (
                <MenuItem key={statusCode} value={statusCode}>
                  {TypeActiveStatusLabel[statusCode]}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </SchedulingSearchCard>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Ativo</th>
              <th>PartNumber</th>
              <th>Condição</th>
              <th>Criação</th>
              <th>Status</th>
              {isAdmin && <th>Empresa</th>}
              {!isUser && <th>Ações</th>}
            </tr>
          </thead>

          <tbody>
            {tools.map((tool) => {
              return (
                <tr key={tool.tools_id}>
                  <td>{tool.tools_name}</td>
                  <td>{tool.tools_part_number}</td>
                  <StatusToolsCell status={tool.tools_condition!}>
                    {ToolsConditionStatusLabel[tool.tools_condition!]}
                  </StatusToolsCell>
                  <td>
                    {new Date(tool.created_at!).toLocaleDateString("pt-BR")}
                  </td>
                  {isAdmin ? (
                    <td>
                      {" "}
                      <StatusSwitch
                        checked={tool.tools_status === 1}
                        onToggle={() => handleToggleStatus(tool)}
                      />
                    </td>
                  ) : tool.tools_status === 1 ? (
                    <StatusMyNotify status={tool.tools_status === 1}>
                      Ativo
                    </StatusMyNotify>
                  ) : (
                    <StatusMyNotify status={tool.tools_status === 1}>
                      Inativo
                    </StatusMyNotify>
                  )}
                  {isAdmin && <td>{`${tool.company!.company_name}`}</td>}
                  {!isUser && (
                    <ActionsCell>
                      <CustomTooltip title="Editar Ativo">
                        <ActionButton onClick={() => handleOpenEditModal(tool)}>
                          <FiEdit size={20} color="#4caf50" />
                        </ActionButton>
                      </CustomTooltip>

                      <CustomTooltip title="Excluir Ativo">
                        <ActionButton onClick={() => openDeleteDialog(tool)}>
                          <FiTrash2 size={20} color="#f44336" />
                        </ActionButton>
                      </CustomTooltip>
                    </ActionsCell>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        setPage={setPage}
      />

      <Dialog open={dialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>

        <DialogActions>
          <Button onClick={handleCancelDelete} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="primary"
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <ToolsCreateModal
        open={isModalOpen}
        onClose={handleCloseModal}
        tools={selectedTools}
      />
    </Container>
  );
};
